import NotFoundPage from './NotFoundPage.jsx';
import { usePublicMetadata } from '../seo/PublicMetadata.jsx';
import { useInitialPublicData } from '../seo/PublicBootstrap.jsx';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Search, Store, Ticket } from 'lucide-react';
import { Button } from '@mts/ui';
import OfferCard from '../components/offers/OfferCard.jsx';
import { usePublicTenant } from '../hooks/usePublicTenant';
import { getOffersPage, trackOfferEvent } from '../services/offersService';
import {
  DEAL_FILTERS,
  DEAL_SORTS,
  filterOffers,
  isFeaturedOffer,
  selectDeals,
} from '../utils/deals';
import { buildPublicPageMetadata } from '../utils/publicMetadata.js';
import { buildBreadcrumbSchema, buildItemListSchema, buildWebPageSchema } from '../utils/structuredData.js';

function cleanString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function getSettingValue(tenant, key) {
  const list = Array.isArray(tenant?.tenantSettings) ? tenant.tenantSettings : [];
  return list.find((item) => item?.settingKey === key)?.settingValue;
}

function resolveMarketName({ tenant, spotlightName }) {
  return (
    cleanString(tenant?.city) ||
    cleanString(getSettingValue(tenant, 'tenant.city')) ||
    cleanString(spotlightName) ||
    'your community'
  );
}

function useDeals(categorySlug) {
  const initial = useInitialPublicData("offers");
  const initialCategory = useInitialPublicData("offerCategory");
  const [category, setCategory] = useState(initialCategory || null);
  const [offers, setOffers] = useState(initial || []);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (initial && reloadKey === 0) { setOffers(initial); setCategory(initialCategory || null); setLoading(false); setError(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);

    getOffersPage(categorySlug ? { categorySlug } : {})
      .then((page) => {
        if (!cancelled) { setOffers(page.offers); setCategory(page.category); }
      })
      .catch((err) => {
        if (!cancelled) {
          setOffers([]);
          setCategory(null);
          setError(err instanceof Error ? err : new Error('Unable to load deals right now.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [categorySlug, reloadKey, initial, initialCategory]);

  return {
    offers,
    category,
    loading,
    error,
    retry: () => setReloadKey((value) => value + 1),
  };
}

function DealsHero({ categoryName, marketName, searchQuery, onSearchChange, totalCount, resultCount }) {
  return (
    <section className="border-b border-border bg-surface">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
        <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr),360px] lg:items-end">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">Local Savings</p>
            <h1 className="mt-3 max-w-4xl text-4xl font-bold leading-tight text-text-primary sm:text-5xl">
              {categoryName ? `${categoryName} Deals & Offers` : 'Local Deals & Offers'}
            </h1>
            <p className="mt-4 max-w-3xl text-lg leading-8 text-text-secondary">
              Save at local businesses near {marketName}. Browse current offers, compare what is nearby, and open the deal when you are ready.
            </p>
            <div className="mt-5 flex flex-wrap gap-3 text-sm text-text-secondary">
              <span className="inline-flex items-center gap-2 rounded-md border border-border bg-[var(--card,#FFFFFF)] px-3 py-2">
                <Ticket className="h-4 w-4 text-primary" aria-hidden="true" />
                {resultCount} of {totalCount} active deal{totalCount === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center gap-2 rounded-md border border-border bg-[var(--card,#FFFFFF)] px-3 py-2">
                <Store className="h-4 w-4 text-primary" aria-hidden="true" />
                Tenant-curated local offers
              </span>
            </div>
          </div>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-text-primary">Search deals</span>
            <span className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-secondary" aria-hidden="true" />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Search by offer or business"
                className="h-12 w-full rounded-lg border border-border bg-[var(--card,#FFFFFF)] pl-10 pr-4 text-sm text-text-primary outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
              />
            </span>
          </label>
        </div>
      </div>
    </section>
  );
}

function DealsFilterBar({ filter, onFilterChange, sort, onSortChange, counts }) {
  const filters = [
    { key: DEAL_FILTERS.ALL, label: 'All', count: counts.all },
    { key: DEAL_FILTERS.FEATURED, label: 'Featured', count: counts.featured },
    { key: DEAL_FILTERS.ENDING_SOON, label: 'Ending Soon', count: counts.endingSoon },
  ];

  return (
    <section className="sticky top-16 z-30 border-b border-border bg-[var(--card,#FFFFFF)]/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
        <div className="flex gap-2 overflow-x-auto">
          {filters.map((item) => {
            const isActive = filter === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => onFilterChange(item.key)}
                className={[
                  'inline-flex h-9 shrink-0 items-center gap-2 rounded-md border px-3 text-sm font-semibold transition',
                  isActive
                    ? 'border-primary bg-primary text-white hover:text-[var(--primary-soft,#DBEAFE)]'
                    : 'border-border bg-surface text-text-secondary hover:border-primary hover:text-primary',
                ].join(' ')}
              >
                <span>{item.label}</span>
                <span className={isActive ? 'text-white/80' : 'text-text-secondary/80'}>{item.count}</span>
              </button>
            );
          })}
        </div>

        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <span className="font-semibold text-text-primary">Sort</span>
          <select
            value={sort}
            onChange={(event) => onSortChange(event.target.value)}
            className="h-9 rounded-md border border-border bg-surface px-3 text-sm text-text-primary outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
          >
            <option value={DEAL_SORTS.FEATURED}>Featured first</option>
            <option value={DEAL_SORTS.NEWEST}>Newest</option>
            <option value={DEAL_SORTS.ENDING_SOON}>Ending soon</option>
          </select>
        </label>
      </div>
    </section>
  );
}

function DealsSkeletonGrid() {
  return (
    <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" aria-label="Loading deals">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="h-[390px] animate-pulse rounded-lg border border-border bg-[var(--card,#FFFFFF)]">
          <div className="h-44 rounded-t-lg bg-surface" />
          <div className="space-y-4 p-5">
            <div className="h-4 w-28 rounded bg-surface" />
            <div className="h-6 w-4/5 rounded bg-surface" />
            <div className="h-4 w-full rounded bg-surface" />
            <div className="h-4 w-2/3 rounded bg-surface" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyDealsState({ hasFilters, onClear }) {
  return (
    <div className="rounded-lg border border-border bg-[var(--card,#FFFFFF)] p-8 text-center shadow-sm">
      <h2 className="text-2xl font-bold text-text-primary">No active deals right now</h2>
      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-text-secondary">
        {hasFilters
          ? 'Try a different search or filter to see more local offers.'
          : 'Check back soon for new local savings, or browse the business directory today.'}
      </p>
      <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
        {hasFilters ? (
          <Button type="button" variant="outline" onClick={onClear}>Clear Filters</Button>
        ) : null}
        <Button asChild variant="primary">
          <Link to="/listings">Browse Businesses</Link>
        </Button>
      </div>
    </div>
  );
}

function ErrorDealsState({ message, onRetry }) {
  return (
    <div className="rounded-lg border border-border bg-[var(--card,#FFFFFF)] p-8 text-center shadow-sm">
      <h2 className="text-2xl font-bold text-text-primary">Deals could not load</h2>
      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-text-secondary">
        {message || 'Something went wrong while loading current offers.'}
      </p>
      <Button type="button" variant="primary" className="mt-6" onClick={onRetry}>Try Again</Button>
    </div>
  );
}

function BusinessCtaBand() {
  return (
    <section className="mx-auto max-w-6xl px-4 pb-10 sm:px-6 lg:px-8 lg:pb-12">
      <div className="rounded-lg border border-border bg-[var(--card,#FFFFFF)] p-6 shadow-sm md:flex md:items-center md:justify-between md:gap-6">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Want your business featured here?</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-text-secondary">
            Claim your listing or choose a visibility plan to publish offers local customers can find.
          </p>
        </div>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row md:mt-0">
          <Button asChild variant="primary">
            <Link to="/advertise/spotlight">View Pricing</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/claim/new">Claim Listing</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

function DealsPage() {
  const { categorySlug } = useParams();
  const { tenant, spotlightName, tenantLogoConfig, tenantLogoImageUrl } = usePublicTenant();
  const { offers, category, loading, error, retry } = useDeals(categorySlug);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState(DEAL_FILTERS.ALL);
  const [sort, setSort] = useState(DEAL_SORTS.FEATURED);
  const marketName = resolveMarketName({ tenant, spotlightName });

  usePublicMetadata(() => {
    if (!tenant) return;
    const path = categorySlug ? `/offers/category/${encodeURIComponent(categorySlug)}` : '/offers';
    const categoryName = category?.name || '';
    const title = categoryName ? `${categoryName} Deals in ${marketName} | ${spotlightName}` : `${marketName} Deals & Local Offers | ${spotlightName}`;
    const description = categoryName ? `Browse ${categoryName.toLowerCase()} deals and local offers available around ${marketName}.` : `Discover current deals and special offers from local businesses in ${marketName}.`;
    const metadata = buildPublicPageMetadata({ tenant, pathname: path, canonicalPath: path, siteName: spotlightName, cityName: tenantLogoConfig?.cityName, logoUrl: tenantLogoImageUrl, title, description, jsonLd: [], robots: searchQuery || error || (categorySlug && !loading && !offers.length) ? 'noindex,follow' : 'index,follow' });
    if (metadata.canonicalUrl && !searchQuery) metadata.jsonLd = [buildWebPageSchema({ url: metadata.canonicalUrl, name: title, description, type: 'CollectionPage' }), buildItemListSchema({ url: metadata.canonicalUrl, name: title, items: offers.filter((offer) => offer.slug).map((offer) => ({ name: offer.title, url: `${tenant.canonicalOrigin}/offers/${encodeURIComponent(offer.slug)}` })) }), buildBreadcrumbSchema({ url: metadata.canonicalUrl, items: [{ name: 'Home', url: tenant.canonicalOrigin }, { name: 'Offers', url: `${tenant.canonicalOrigin}/offers` }, ...(categoryName ? [{ name: categoryName, url: metadata.canonicalUrl }] : [])] })];
    return (metadata);
  }, [category?.name, error, loading, categorySlug, marketName, offers, searchQuery, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl]);

  const counts = useMemo(() => ({
    all: offers.length,
    featured: filterOffers(offers, { filter: DEAL_FILTERS.FEATURED }).length,
    endingSoon: filterOffers(offers, { filter: DEAL_FILTERS.ENDING_SOON }).length,
  }), [offers]);

  const visibleOffers = useMemo(() => selectDeals(offers, { filter, searchQuery, sort }), [filter, offers, searchQuery, sort]);
  const featuredOffers = useMemo(() => visibleOffers.filter(isFeaturedOffer).slice(0, 3), [visibleOffers]);
  const hasFilters = Boolean(searchQuery.trim()) || filter !== DEAL_FILTERS.ALL;

  const clearFilters = () => {
    setSearchQuery('');
    setFilter(DEAL_FILTERS.ALL);
    setSort(DEAL_SORTS.FEATURED);
  };
  const recordImpression = ({ offer, placementId, variant }) => {
    if (!offer?.id) return;
    trackOfferEvent(offer.id, 'IMPRESSION', { placementId, metadataJson: { surface: 'offer_directory', variant } }).catch(() => null);
  };

  if (error?.status === 404) return <NotFoundPage resource="offer category" />;

  return (
    <div className="bg-surface text-text-primary">
      <DealsHero
        categoryName={category?.name}
        marketName={marketName}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        totalCount={offers.length}
        resultCount={visibleOffers.length}
      />

      <DealsFilterBar
        filter={filter}
        onFilterChange={setFilter}
        sort={sort}
        onSortChange={setSort}
        counts={counts}
      />

      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
        {loading ? (
          <DealsSkeletonGrid />
        ) : error ? (
          <ErrorDealsState message={error?.message} onRetry={retry} />
        ) : visibleOffers.length === 0 ? (
          <EmptyDealsState hasFilters={hasFilters} onClear={clearFilters} />
        ) : (
          <>
            {featuredOffers.length > 0 ? (
              <div className="mb-10">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <h2 className="text-2xl font-bold text-text-primary">Featured Deals</h2>
                  <span className="text-sm text-text-secondary">{featuredOffers.length} highlighted</span>
                </div>
                <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                  {featuredOffers.map((offer) => (
                    <OfferCard key={offer.id || offer.slug} offer={offer} variant="featured" onImpression={recordImpression} />
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mb-4 flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-text-primary">All Active Deals</h2>
              <span className="text-sm text-text-secondary">{visibleOffers.length} result{visibleOffers.length === 1 ? '' : 's'}</span>
            </div>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {visibleOffers.map((offer) => (
                <OfferCard key={offer.id || offer.slug} offer={offer} onImpression={recordImpression} />
              ))}
            </div>
          </>
        )}
      </section>

      <BusinessCtaBand />
    </div>
  );
}

export default DealsPage;
